import type { Distance, Point } from "@pdfking/math"
import { Hypot, Max, Min, Scale, Total } from "@pdfking/math"
import { Range } from "@pdfking/tfunc"

import type { FolderCorner } from "#internal"

import { FolderOutline } from "./FolderOutline"

// Points along each curve.
const STEPS = 8

/** Whether a point lies inside the outline round `corners`, rounded as
 * drawn, `margin` or more from its edge. */
export const FolderInside = (corners: FolderCorner[], margin: Distance) => {
  const { points } = FolderOutline(corners)
  const polygon = points.flatMap((p0, i) => {
    const p1 = points[(i + 1) % points.length]!
    const [h0, h1] = [p0.after, p1.before]
    return [...Range(STEPS)].map(step => {
      const t = step / STEPS
      const [a, b, c, d] = [(1 - t) ** 3, 3 * (1 - t) ** 2 * t, 3 * (1 - t) * t ** 2, t ** 3]
      return {
        x: Total(Scale(p0.x, a), Scale(h0.x, b), Scale(h1.x, c), Scale(p1.x, d)),
        y: Total(Scale(p0.y, a), Scale(h0.y, b), Scale(h1.y, c), Scale(p1.y, d)),
      }
    })
  })
  const edges = polygon.map((p, i) => [p, polygon[(i + 1) % polygon.length]!] as const)
  const distance = ({ x, y }: Point<Distance>, [p, q]: (typeof edges)[number]) => {
    const length = (q.x - p.x) ** 2 + (q.y - p.y) ** 2
    const t = length
      ? Max(0, Min(1, ((x - p.x) * (q.x - p.x) + (y - p.y) * (q.y - p.y)) / length))
      : 0
    return Hypot(x - p.x - t * (q.x - p.x), y - p.y - t * (q.y - p.y))
  }
  return (point: Point<Distance>): boolean => {
    const crossings = edges.filter(
      ([p, q]) =>
        p.y > point.y !== q.y > point.y &&
        point.x < p.x + ((point.y - p.y) * (q.x - p.x)) / (q.y - p.y)
    ).length
    return crossings % 2 === 1 && edges.every(edge => distance(point, edge) >= margin)
  }
}
